import React, { useEffect, useMemo, useState } from 'react';
import { Download, Share2, X, Smartphone } from 'lucide-react';
import { useLanguage } from '../utils/i18n';

const DISMISS_KEY = 'pwaInstallDismissedUntil';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const getDismissedUntil = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const parsed = Number(raw || '0');
    return Number.isFinite(parsed) ? parsed : 0;
  } catch (error) {
    return 0;
  }
};

const setDismissedUntil = (timestamp) => {
  try {
    localStorage.setItem(DISMISS_KEY, String(timestamp));
  } catch (error) {}
};

const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIOSDevice = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const isSafariBrowser = () => {
  const ua = window.navigator.userAgent;
  return /safari/i.test(ua) && !/chrome|crios|android|fxios/i.test(ua);
};

export default function PWAInstallPrompt() {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() =>
    typeof window === 'undefined' ? false : isStandaloneMode()
  );
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return getDismissedUntil() > Date.now();
  });

  const isIOS = useMemo(() => isIOSDevice(), []);
  const isSafari = useMemo(() => isSafariBrowser(), []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setIsStandalone(true);
      setDeferredPrompt(null);
      setDismissedUntil(0);
      setIsDismissed(false);
    };

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => setIsStandalone(isStandaloneMode());

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const dismissPrompt = (ttlMs = DISMISS_TTL_MS) => {
    const until = Date.now() + ttlMs;
    setDismissedUntil(until);
    setIsDismissed(true);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);

    if (result?.outcome === 'accepted') {
      setIsStandalone(true);
      setDismissedUntil(0);
      setIsDismissed(false);
    } else {
      dismissPrompt(24 * 60 * 60 * 1000);
    }
  };

  const showIOSInstructions = isIOS && isSafari;
  const canTriggerInstall = Boolean(deferredPrompt);
  const shouldShow = !isStandalone && !isDismissed && (canTriggerInstall || showIOSInstructions);

  if (!shouldShow) return null;

  return (
    <section className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%] bg-slate-900/90 border border-sky-500/30 backdrop-blur-2xl p-4 rounded-2xl shadow-2xl flex items-center justify-between gap-4 text-white">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-sky-500/20 text-sky-400 rounded-xl border border-sky-400/30">
          <Smartphone size={24} />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">{t('installApp')}</h3>
          <p className="text-xs text-slate-300 max-w-xs">{t('pwaPrompt')}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {canTriggerInstall ? (
          <button
            onClick={handleInstallClick}
            className="flex items-center gap-1.5 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-lg transition transform hover:scale-105"
          >
            <Download size={14} />
            <span>Install</span>
          </button>
        ) : (
          <div className="flex items-center gap-1.5 bg-slate-800 text-xs text-sky-300 px-3 py-2 rounded-xl border border-white/10">
            <Share2 size={14} />
            <span>Share &gt; Add to Home</span>
          </div>
        )}
        <button
          onClick={() => dismissPrompt()}
          className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white"
        >
          <X size={16} />
        </button>
      </div>
    </section>
  );
}
